import {
  isGroupedExportVariant,
  type DownloadSettingsModalVariant,
} from '@/app/(main)/client-detail/types/downloadSettingsModalVariant';
import type { SectionConfig, SectionKey } from '../types';

// Group view reuses API field keys under different section labels (e.g. email under "Contact information").
// UI-only section keys (`group_view_*`) only drive toggles; see `uiOnlySectionKeys.ts`.

export const SECTIONS_DEFAULT: SectionConfig[] = [
  {
    key: 'include_basic_information',
    label: 'Basic information',
    columns: 2,
    subItems: [
      { key: 'include_basic_information_first_name', label: 'First Name' },
      { key: 'include_basic_information_middle_name', label: 'Middle Name' },
      { key: 'include_basic_information_last_name', label: 'Last Name' },
      { key: 'include_basic_information_primary_email', label: 'Primary Email' },
      { key: 'include_basic_information_date_of_birth', label: 'DOB' },
      { key: 'include_basic_information_phone', label: 'Phone Number' },
      { key: 'include_basic_information_gender', label: 'Sex' },
      { key: 'include_basic_information_home_airports', label: 'Home Airport(s)' },
    ],
  },
  {
    key: 'include_address',
    label: 'Address',
    subItems: [
      { key: 'include_address_mailing', label: 'Mailing' },
      { key: 'include_address_billing', label: 'Billing' },
    ],
  },
  { key: 'include_allergies_dietary_restrictions', label: 'Allergies & Dietary Restrictions' },
  {
    key: 'include_important_dates',
    label: 'Important Dates',
    subItems: [
      { key: 'include_important_dates_date_of_birth', label: 'Birthday' },
      { key: 'include_important_dates_confirmation_ceremony', label: 'Confirmation ceremony' },
    ],
  },
  {
    key: 'include_loyalty_programs',
    label: 'Loyalty Programs',
    columns: 2,
    subItems: [
      { key: 'include_loyalty_program_full_name', label: 'Full name' },
      { key: 'include_loyalty_program_program', label: 'Program' },
      { key: 'include_loyalty_program_number', label: 'Number' },
    ],
  },
  {
    key: 'include_important_travel_data',
    label: 'Important Travel Data',
    subItems: [
      { key: 'include_important_travel_data_global_entry_number', label: 'Global entry number' },
      { key: 'include_important_travel_data_known_traveler_number', label: 'KTN' },
    ],
  },
  { key: 'include_emergency_contact', label: 'Emergency Contact' },
  { key: 'include_flight_preferences', label: 'Flight Preferences' },
  {
    key: 'include_travel_preferences_by_category',
    label: 'Travel Preferences by Category',
    columns: 2,
    subItems: [
      { key: 'include_travel_preferences_by_category_travel', label: 'Travel' },
      { key: 'include_travel_preferences_by_category_lifestyle', label: 'Lifestyle' },
      { key: 'include_travel_preferences_by_category_stay', label: 'Stay' },
      { key: 'include_travel_preferences_by_category_personal', label: 'Personal' },
    ],
  },
  { key: 'include_related_contacts', label: 'Related contacts' },
  { key: 'include_notes', label: 'Notes' },
  { key: 'include_tasks', label: 'Tasks' },
  { key: 'include_client_documents', label: 'Client documents' },
];

const GROUP_VIEW_TAIL_SECTION_KEYS = [
  'include_important_dates',
  'include_loyalty_programs',
  'include_important_travel_data',
] as const satisfies readonly SectionKey[];

const SECTIONS_GROUP_VIEW_TAIL: SectionConfig[] = GROUP_VIEW_TAIL_SECTION_KEYS.map((key) => {
  const section = SECTIONS_DEFAULT.find((s) => s.key === key);
  if (!section) {
    throw new Error(`DownloadSettingsModal: SECTIONS_DEFAULT missing ${key}`);
  }
  return section;
});

export const SECTIONS_GROUP_VIEW: SectionConfig[] = [
  {
    key: 'include_basic_information',
    label: 'Basic information',
    columns: 2,
    subItems: [
      { key: 'include_basic_information_first_name', label: 'First Name' },
      { key: 'include_basic_information_middle_name', label: 'Middle Name' },
      { key: 'include_basic_information_last_name', label: 'Last Name' },
      { key: 'include_basic_information_gender', label: 'Gender' },
      { key: 'include_basic_information_date_of_birth', label: 'Date of Birth' },
      { key: 'include_basic_information_age', label: 'Age' },
      { key: 'include_basic_information_relationship_to_primary', label: 'Relationship to Primary' },
    ],
  },
  {
    key: 'include_address',
    label: 'Contact information',
    columns: 2,
    subItems: [
      { key: 'include_basic_information_primary_email', label: 'Primary Email' },
      { key: 'include_basic_information_phone', label: 'Phone Number' },
      { key: 'include_address_mailing', label: 'Mailing address' },
    ],
  },
  {
    key: 'group_view_preferences_allergies',
    label: 'Preferences & Allergies',
    columns: 2,
    subItems: [
      { key: 'include_flight_preferences', label: 'Seat Preference' },
      { key: 'include_allergies_dietary_restrictions', label: 'Allergies & Dietary Restrictions' },
    ],
  },
  {
    key: 'include_passport_details',
    label: 'Passport details',
    columns: 2,
    subItems: [
      { key: 'include_passport_details_passport_number', label: 'Passport number' },
      { key: 'include_passport_details_expiry_date', label: 'Expiry date' },
    ],
  },
  {
    key: 'group_view_comfort_related',
    label: 'Comfort Related Details',
    columns: 2,
    subItems: [
      { key: 'include_comfort_related_full_name', label: 'Full name' },
      { key: 'include_comfort_related_height', label: 'Height' },
      { key: 'include_comfort_related_weight', label: 'Weight' },
      { key: 'include_comfort_related_details', label: 'Comfort-related details' },
    ],
  },
  ...SECTIONS_GROUP_VIEW_TAIL,
];

export function getSectionsForVariant(variant: DownloadSettingsModalVariant): SectionConfig[] {
  return isGroupedExportVariant(variant) ? SECTIONS_GROUP_VIEW : SECTIONS_DEFAULT;
}
